import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "../../components/Button";
import ButtonDropdown from "../../components/ButtonDropdown";
import Icon from "../../components/Icon";
import Modal from "../../components/Modal";
import Pagination from "../../components/Pagination";
import Toggle from "../../components/Toggle";
import SupplierProductPicker, { ProductThumb } from "./SupplierProductPicker";

import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { canEditBrands, hasPermission } from "../../services/auth";
import { UNAVAILABLE_REASONS, describeCustomizedFields } from "../../helpers/personalizedProduct";

const PER_PAGE = 15;

const FILTERS = [
    { label: 'Todos', value: '', count: 'all' },
    { label: 'Ligados', value: 'on_site', count: 'on_site' },
    { label: 'Desligados', value: 'off_site', count: 'off_site' }
];

const NEW_PRODUCTS_MODES = [
    { value: true, label: 'Entram no site automaticamente', hint: 'O que você cadastrar depois já aparece no site da marca.' },
    { value: false, label: 'Ficam fora do site até você ligar', hint: 'O que você cadastrar depois espera você ligar aqui na lista.' }
];

const formatCount = value => Number(value ?? 0).toLocaleString('pt-br');

export const ProductsTab = ({ brandId, brandName, catalogMode, onCatalogModeChange }) => {
    const navigate = useNavigate();
    const canEdit = canEditBrands();
    const canCreateProduct = hasPermission('create_product');
    const siteName = `site da ${brandName ?? 'marca'}`;

    const [newProductsOnSite, setNewProductsOnSite] = useState((catalogMode ?? 'all') === 'all');
    const [savingDefault, setSavingDefault] = useState(false);
    const [modeChoice, setModeChoice] = useState(null);

    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [counts, setCounts] = useState({});
    const [selection, setSelection] = useState({ turn_on: 0, turn_off: 0 });
    // Contagem sem busca: é a frase do topo ("X de Y no site") e não pode
    // mudar enquanto o usuário digita na busca.
    const [totals, setTotals] = useState(null);
    const [filter, setFilter] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);

    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [supplier, setSupplier] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [personalizedOnly, setPersonalizedOnly] = useState(false);
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);

    const [showPicker, setShowPicker] = useState(false);
    const [showAddChoice, setShowAddChoice] = useState(false);
    const [personalizing, setPersonalizing] = useState(null);
    const [toggling, setToggling] = useState([]);
    const [bulkAction, setBulkAction] = useState(null);
    const [savingBulk, setSavingBulk] = useState(false);

    const narrowed = Boolean(appliedSearch || supplier || categoryId || personalizedOnly);
    const listParams = { search: appliedSearch, filter, supplier, category_id: categoryId, personalized: personalizedOnly ? 1 : undefined };

    useEffect(() => {
        getProducts();
    }, [currentPage, appliedSearch, filter, supplier, categoryId, personalizedOnly]);

    useEffect(() => {
        getFilterOptions();
    }, []);

    const getFilterOptions = () => {
        api.get(`/brand/${brandId}/catalog/filters`).then(res => {
            setSupplierOptions(res.data.suppliers.map(option => ({
                ...option,
                label: `${option.label} (${formatCount(option.total)})`,
                name: option.label
            })));
        }).catch(() => {});

        api.get(`/category`).then(res => {
            const options = [];
            res.data.resources.forEach(category => {
                options.push({ value: category.id, label: category.name, name: category.name });
                (category.categories ?? []).forEach(subcategory => {
                    options.push({ value: subcategory.id, label: `— ${subcategory.name}`, name: subcategory.name });
                });
            });
            setCategoryOptions(options);
        }).catch(() => {});
    };

    const getProducts = () => {
        setLoading(true);

        api.get(`/brand/${brandId}/catalog`, {
            params: { page: currentPage, per_page: PER_PAGE, ...listParams }
        }).then(res => {
            const resources = res.data.resources;
            setProducts(resources.data ?? []);
            setCounts(res.data.counts);
            setSelection(res.data.selection);
            if (!narrowed) setTotals(res.data.counts);
            setNewProductsOnSite(res.data.new_products_on_site);
            setLastPage(resources.last_page);
            setTotalResults(resources.total);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    };

    // A busca e o filtro sempre voltam para a primeira página: manter a página
    // atual levaria a um resultado vazio quando sobram menos páginas.
    const applySearch = () => {
        if (currentPage !== 1) setCurrentPage(1);
        setAppliedSearch(search.trim());
    };

    const changeFilter = value => {
        if (currentPage !== 1) setCurrentPage(1);
        setFilter(value);
    };

    const changeSupplier = value => {
        if (currentPage !== 1) setCurrentPage(1);
        setSupplier(value);
    };

    const changeCategory = value => {
        if (currentPage !== 1) setCurrentPage(1);
        setCategoryId(value);
    };

    const changePersonalizedOnly = value => {
        if (currentPage !== 1) setCurrentPage(1);
        setPersonalizedOnly(value);
    };

    const clearSearch = () => {
        if (currentPage !== 1) setCurrentPage(1);
        setSearch('');
        setAppliedSearch('');
    };

    const clearNarrowing = () => {
        clearSearch();
        setSupplier('');
        setCategoryId('');
        setPersonalizedOnly(false);
    };

    // O recorte por extenso ("SPOT · Canetas · busca "inox""): a ação em massa
    // e a confirmação dizem sobre o que vão agir, não "desta lista".
    const describeSelection = () => {
        const parts = [];
        const supplierName = supplierOptions.find(option => option.value === supplier)?.name;
        const categoryName = categoryOptions.find(option => option.value === categoryId)?.name;

        if (supplierName) parts.push(supplierName);
        if (categoryName) parts.push(categoryName);
        if (appliedSearch) parts.push(`busca "${appliedSearch}"`);
        if (filter === 'on_site') parts.push('ligados');
        if (filter === 'off_site') parts.push('desligados');

        return parts.length ? parts.join(' · ') : 'Todos os produtos';
    };

    const saveNewProductsMode = () => {
        const value = modeChoice;

        if (value === newProductsOnSite) {
            setModeChoice(null);
            return;
        }

        setSavingDefault(true);

        api.put(`/brand/${brandId}/catalog-mode`, { catalog_mode: value ? 'all' : 'selected' }).then(() => {
            setNewProductsOnSite(value);
            setModeChoice(null);
            toast.success(value
                ? `Produtos novos vão entrar no ${siteName} automaticamente`
                : `Produtos novos vão ficar fora do ${siteName} até você ligar`);
            if (onCatalogModeChange) onCatalogModeChange(value ? 'all' : 'selected');
        }).catch(error => {
            renderError(error);
        }).then(() => setSavingDefault(false));
    };

    const shiftCounts = (current, onSite) => {
        if (!current) return current;
        const delta = onSite ? 1 : -1;
        return { ...current, on_site: current.on_site + delta, off_site: current.off_site - delta };
    };

    const toggleProduct = (product, onSite) => {
        setToggling(prev => [...prev, product.id]);
        setProducts(prev => prev.map(item => item.id === product.id ? { ...item, on_site: onSite } : item));
        setCounts(prev => shiftCounts(prev, onSite));
        setTotals(prev => shiftCounts(prev, onSite));

        api.put(`/brand/${brandId}/product/${product.id}/site`, { on_site: onSite }).catch(error => {
            setProducts(prev => prev.map(item => item.id === product.id ? { ...item, on_site: !onSite } : item));
            setCounts(prev => shiftCounts(prev, !onSite));
            setTotals(prev => shiftCounts(prev, !onSite));
            renderError(error);
        }).then(() => setToggling(prev => prev.filter(id => id !== product.id)));
    };

    const applyBulk = () => {
        setSavingBulk(true);

        api.put(`/brand/${brandId}/catalog/site`, {
            on_site: bulkAction === 'on',
            ...listParams
        }).then(res => {
            toast.success(bulkAction === 'on'
                ? `${formatCount(res.data.affected)} produtos ligados no ${siteName}`
                : `${formatCount(res.data.affected)} produtos desligados do ${siteName}`);
            setBulkAction(null);
            setTotals(null);
            getProducts();
            if (narrowed) refreshTotals();
        }).catch(error => {
            renderError(error);
        }).then(() => setSavingBulk(false));
    };

    const refreshTotals = () => {
        api.get(`/brand/${brandId}/catalog`, { params: { per_page: 1 } }).then(res => setTotals(res.data.counts));
    };

    const personalize = product => {
        setPersonalizing(product.id);

        api.post(`/brand/${brandId}/personalize`, { product_id: product.id }).then(res => {
            navigate(`/brand/${brandId}/personalized/${res.data.resource.id}`);
        }).catch(error => {
            renderError(error);
            setPersonalizing(null);
        });
    };

    const renderStatus = product => {
        const personalized = product.personalized;

        if (personalized?.unavailable_reason) return <span className="brand-status-badge pending">Indisponível</span>;
        if (personalized) return <span className="brand-status-badge personalized">Personalizado</span>;
        if (product.exclusive) return <span className="brand-status-badge catalog">Fora do site principal</span>;

        return null;
    };

    const renderRow = product => {
        const personalized = product.personalized;
        const reason = personalized?.unavailable_reason;
        const openEditor = () => navigate(`/brand/${brandId}/personalized/${product.id}`);

        return (
            <div className={`brand-product-row ${reason ? 'unavailable' : ''} ${product.on_site ? '' : 'off-site'}`} key={product.id}>
                <ProductThumb image={product.image} name={product.name} />

                <div className="brand-product-info">
                    <strong>{product.name}</strong>
                    {personalized
                        ? <>
                            <span>{describeCustomizedFields(personalized.customized_fields)}</span>
                            {reason
                                ? <span className="brand-product-alert"><Icon name="alert" size={13} /> {UNAVAILABLE_REASONS[reason]}</span>
                                : personalized.source &&
                                    <span className="brand-product-source">
                                        <Icon name="link" size={13} /> Substitui {personalized.source.name} ({product.supplier} {personalized.source.company_sku}) neste site
                                    </span>
                            }
                        </>
                        : <span>
                            {product.kind === 'supplier'
                                ? `${product.supplier} · SKU ${product.company_sku}`
                                : `SKU ${product.sku}`}
                            {` · estoque ${formatCount(product.stock)}`}
                        </span>
                    }
                </div>

                <div className="brand-product-badges">{renderStatus(product)}</div>

                <div className="brand-product-actions">
                    {personalized &&
                        <Button type="secondary" size="small" text={reason ? 'Resolver' : 'Editar'} action={openEditor} />
                    }
                    {!personalized && product.kind === 'supplier' && canEdit &&
                        <Button
                            type="secondary"
                            size="small"
                            svg="design"
                            text="Personalizar"
                            loading={personalizing === product.id}
                            action={() => personalize(product)}
                        />
                    }
                    {!personalized && product.kind !== 'supplier' &&
                        <Button type="secondary" size="small" text="Abrir" action={() => navigate(`/product/edit/${product.id}`)} />
                    }
                </div>

                <div className="brand-product-site">
                    <Toggle
                        checked={product.on_site}
                        disabled={!canEdit || toggling.includes(product.id)}
                        label={`${product.name} no ${siteName}`}
                        onChange={value => toggleProduct(product, value)}
                    />
                </div>
            </div>
        );
    };

    const bulkCount = bulkAction === 'on' ? selection.turn_on : selection.turn_off;
    const productsLabel = count => `${formatCount(count)} ${count === 1 ? 'produto' : 'produtos'}`;

    return (
        <div className="brand-products">
            <SupplierProductPicker
                show={showPicker}
                close={() => setShowPicker(false)}
                brandId={brandId}
                title={`Personalizar para ${brandName ?? 'a marca'}`}
                actionText="Personalizar"
                picking={personalizing}
                onPick={personalize}
            />
            <Modal
                className="brand-confirm-modal"
                title={bulkAction === 'on' ? `Ligar produtos no ${siteName}` : `Desligar produtos do ${siteName}`}
                show={bulkAction !== null}
                close={() => setBulkAction(null)}
                footer={
                    <>
                        <Button type="secondary" size="small" text="Cancelar" action={() => setBulkAction(null)} />
                        <Button
                            type="primary"
                            size="small"
                            text={bulkAction === 'on' ? `Ligar ${productsLabel(bulkCount)}` : `Desligar ${productsLabel(bulkCount)}`}
                            loading={savingBulk}
                            action={applyBulk}
                        />
                    </>
                }
            >
                <div className={`brand-confirm ${bulkAction === 'off' ? 'danger' : ''}`}>
                    <span className="brand-confirm-icon">
                        <Icon name={bulkAction === 'on' ? 'eye' : 'alert'} size={22} />
                    </span>
                    <div>
                        <strong>
                            {bulkAction === 'on'
                                ? `${productsLabel(bulkCount)} ${bulkCount === 1 ? 'passa' : 'passam'} a aparecer no ${siteName}.`
                                : `${productsLabel(bulkCount)} ${bulkCount === 1 ? 'sai' : 'saem'} do ${siteName} e ${bulkCount === 1 ? 'deixa' : 'deixam'} de aparecer para os clientes da marca.`}
                        </strong>
                        <p className="brand-confirm-scope">Seleção: {describeSelection()}</p>
                        <ul>
                            {bulkAction === 'on' &&
                                <li>Vale para os produtos de hoje. Os que chegarem depois seguem a opção "Produtos novos", no topo da aba.</li>
                            }
                            {bulkAction === 'off' &&
                                <li>Nada é apagado: dá para colocar de volta a qualquer momento.</li>
                            }
                            {counts.personalized > 0 &&
                                <li>Versões personalizadas não mudam: elas se publicam uma a uma.</li>
                            }
                        </ul>
                    </div>
                </div>
            </Modal>

            <Modal
                className="brand-add-product-modal"
                title={`Adicionar produto à ${brandName ?? 'marca'}`}
                show={showAddChoice}
                close={() => setShowAddChoice(false)}
            >
                <div className="brand-add-options">
                    <button
                        type="button"
                        onClick={() => {
                            setShowAddChoice(false);
                            setShowPicker(true);
                        }}
                    >
                        <span className="brand-add-icon"><Icon name="design" size={20} /></span>
                        <strong>A partir de um produto de fornecedor</strong>
                        <span>Use um produto do catálogo com a foto, o nome e a descrição da marca. Preço, estoque e cores continuam vindo do fornecedor.</span>
                    </button>
                    {canCreateProduct &&
                        <button type="button" onClick={() => navigate(`/product/create?brand=${brandId}`)}>
                            <span className="brand-add-icon"><Icon name="product" size={20} /></span>
                            <strong>Cadastro próprio</strong>
                            <span>Um produto que não vem de fornecedor, como um kit montado para a marca. Preço e estoque ficam por sua conta.</span>
                        </button>
                    }
                </div>
            </Modal>

            <Modal
                className="brand-confirm-modal"
                title="Produtos novos"
                show={modeChoice !== null}
                close={() => setModeChoice(null)}
                footer={
                    <>
                        <Button type="secondary" size="small" text="Cancelar" action={() => setModeChoice(null)} />
                        <Button type="primary" size="small" text="Salvar" loading={savingDefault} action={saveNewProductsMode} />
                    </>
                }
            >
                <p className="brand-mode-intro">O que acontece com os produtos que chegarem ao catálogo depois de hoje:</p>
                <div className="brand-mode-options" role="radiogroup">
                    {NEW_PRODUCTS_MODES.map(mode => (
                        <button
                            key={String(mode.value)}
                            type="button"
                            role="radio"
                            aria-checked={modeChoice === mode.value}
                            className={modeChoice === mode.value ? 'active' : ''}
                            onClick={() => setModeChoice(mode.value)}
                        >
                            <span className="brand-mode-dot" />
                            <span>
                                <strong>{mode.label}</strong>
                                <small>{mode.hint}</small>
                            </span>
                        </button>
                    ))}
                </div>
            </Modal>

            <div className="brand-tab-head">
                <div>
                    <h2 className="section-title">Produtos do site da marca</h2>
                    <p className="brand-site-summary">
                        <strong>{totals ? `${formatCount(totals.on_site)} de ${formatCount(totals.all)}` : '—'}</strong> produtos estão ligados e aparecem no {siteName}
                    </p>
                    <p className="brand-new-products">
                        <Icon name="config" size={14} />
                        Produtos novos {newProductsOnSite ? 'entram no site automaticamente' : 'ficam fora do site até você ligar'}
                        {canEdit &&
                            <button type="button" onClick={() => setModeChoice(newProductsOnSite)}>Alterar</button>
                        }
                    </p>
                </div>
                {canEdit &&
                    <Button type="primary" size="small" svg="plus-white" text="Adicionar produto" action={() => setShowAddChoice(true)} />
                }
            </div>

            {canEdit && totals && totals.personalized === 0 &&
                <div className="brand-personalize-invite">
                    <div className="brand-personalize-art" aria-hidden="true">
                        <span><Icon name="product" size={22} /></span>
                        <Icon name="arrow-right-white" size={16} />
                        <span className="branded"><Icon name="design" size={22} /></span>
                    </div>
                    <div className="brand-personalize-text">
                        <strong>Mostre os produtos com a cara da {brandName ?? 'marca'}</strong>
                        <p>Personalize um produto de fornecedor com a foto, o nome e a descrição da marca, ou cadastre um produto próprio só para ela.</p>
                    </div>
                    <Button type="secondary" size="small" svg="design" text="Adicionar produto" action={() => setShowAddChoice(true)} />
                </div>
            }

            <div className="brand-products-filters">
                <div className="brand-search">
                    <input
                        type="text"
                        placeholder="Buscar por nome ou SKU"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && applySearch()}
                    />
                    <button onClick={applySearch}>
                        <Icon name="search" size={16} />
                    </button>
                </div>
                <select value={supplier} onChange={e => changeSupplier(e.target.value)}>
                    <option value="">Todos os fornecedores</option>
                    {supplierOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <select value={categoryId} onChange={e => changeCategory(e.target.value)}>
                    <option value="">Todas as categorias</option>
                    {categoryOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <button
                    type="button"
                    className={`brand-filter-toggle ${personalizedOnly ? 'active' : ''}`}
                    aria-pressed={personalizedOnly}
                    onClick={() => changePersonalizedOnly(!personalizedOnly)}
                >
                    <Icon name={personalizedOnly ? 'check' : 'design'} size={14} />
                    Só personalizados <span>{formatCount(counts.personalized)}</span>
                </button>
            </div>

            {narrowed &&
                <div className="brand-active-filters">
                    {appliedSearch &&
                        <button type="button" onClick={clearSearch}>Busca "{appliedSearch}" <Icon name="close" size={12} /></button>
                    }
                    {supplier &&
                        <button type="button" onClick={() => changeSupplier('')}>
                            {supplierOptions.find(option => option.value === supplier)?.name} <Icon name="close" size={12} />
                        </button>
                    }
                    {categoryId &&
                        <button type="button" onClick={() => changeCategory('')}>
                            {categoryOptions.find(option => option.value === categoryId)?.name} <Icon name="close" size={12} />
                        </button>
                    }
                    {personalizedOnly &&
                        <button type="button" onClick={() => changePersonalizedOnly(false)}>Só personalizados <Icon name="close" size={12} /></button>
                    }
                    <button type="button" className="brand-clear-narrow" onClick={clearNarrowing}>Limpar filtros</button>
                </div>
            }

            <div className="brand-product-list">
                <div className="brand-list-head">
                    <div className="brand-status-segments" role="group" aria-label="Situação no site">
                        {FILTERS.map(option => (
                            <button
                                key={option.value}
                                type="button"
                                className={filter === option.value ? 'active' : ''}
                                aria-pressed={filter === option.value}
                                onClick={() => changeFilter(option.value)}
                            >
                                {option.label} <span>{formatCount(counts[option.count])}</span>
                            </button>
                        ))}
                    </div>
                    {canEdit && !personalizedOnly && (selection.turn_on > 0 || selection.turn_off > 0) &&
                        <ButtonDropdown
                            type="secondary"
                            size="small"
                            text="Ações em massa"
                            actions={[
                                ...(selection.turn_on > 0 ? [{ text: `Ligar todos (${formatCount(selection.turn_on)})`, svg: 'eye', action: () => setBulkAction('on') }] : []),
                                ...(selection.turn_off > 0 ? [{ text: `Desligar todos (${formatCount(selection.turn_off)})`, svg: 'eyeoff', action: () => setBulkAction('off') }] : [])
                            ]}
                        />
                    }
                </div>
                {loading && <p className="brand-product-empty">Carregando...</p>}
                {!loading && products.length === 0 &&
                    <p className="brand-product-empty">
                        {personalizedOnly && !appliedSearch && !supplier && !categoryId ? 'Nenhum produto personalizado ainda.' : 'Nenhum produto encontrado.'}
                    </p>
                }
                {!loading && products.map(renderRow)}
            </div>

            {!loading &&
                <Pagination
                    currentPage={currentPage}
                    lastPage={lastPage}
                    totalResults={totalResults}
                    hasNextPage={currentPage < lastPage}
                    hasPrevPage={currentPage > 1}
                    setCurrentPage={setCurrentPage}
                />
            }
        </div>
    );
};

export default ProductsTab;
